import { useEffect, useState } from "react";
import { ArrowRight, Certificate, PlayCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { CourseCard, LoadingState, MetricGrid } from "@/components/DashboardParts";
import AttendanceCheckIn from "@/components/AttendanceCheckIn";
import LearnerAttendanceCalendar from "@/components/LearnerAttendanceCalendar";
import TrainerFeedbackForm from "@/components/TrainerFeedbackForm";
import RoleLayout from "@/components/RoleLayout";
import { api, formatApiError } from "@/lib/api";

export default function LearnerDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    api.get("/lms/dashboard").then((response) => setDashboard(response.data));
  }, []);

  if (!dashboard) {
    return <LoadingState label="Loading your learning plan" />;
  }

  const downloadManual = async () => {
    setIsDownloading(true);
    try {
      const response = await api.get("/lms/learner/user-manual", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Skillexpress_Learner_User_Manual.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error(formatApiError(error)); } finally { setIsDownloading(false); }
  };

  const currentCourse = [...dashboard.courses].sort((first, second) => second.progress - first.progress)[0];
  const metrics = [
    { key: "assigned", label: "Assigned", value: dashboard.metrics.assigned, hint: "Learning pathways" },
    { key: "in-progress", label: "In progress", value: dashboard.metrics.in_progress, hint: "Active right now" },
    { key: "completed", label: "Completed", value: dashboard.metrics.completed, hint: "Finished pathways" },
    { key: "minutes", label: "Learning minutes", value: dashboard.metrics.learning_minutes, hint: "Applied this cycle" },
  ];

  return (
    <RoleLayout eyebrow="Learner workspace" title="Your learning plan">
      <AttendanceCheckIn />
      <LearnerAttendanceCalendar />
      <TrainerFeedbackForm />
      <div className="manual-download-row"><span>Need help getting started?</span><button className="outline-button" onClick={downloadManual} disabled={isDownloading} data-testid="download-learner-manual-button">{isDownloading ? "Preparing manual" : "Download learner manual"}</button></div>
      <MetricGrid items={metrics} testPrefix="learner" />
      {currentCourse && (
        <section className="continue-band" data-testid="continue-learning-panel">
          <div>
            <p className="eyebrow">Continue where you left off</p>
            <h2 data-testid="continue-learning-title">{currentCourse.title}</h2>
            <p>{currentCourse.description}</p>
            <div className="continue-progress" data-testid="continue-learning-progress">
              <span>Module progress</span><strong>{currentCourse.progress}%</strong>
              <div className="progress-track"><span style={{ width: `${currentCourse.progress}%` }} /></div>
            </div>
          </div>
          <Link className="primary-button" to={`/learner/course/${currentCourse.id}`} data-testid="continue-learning-button">
            <PlayCircle aria-hidden="true" size={21} weight="fill" />
            Resume learning
          </Link>
        </section>
      )}
      <section className="section-heading" data-testid="assigned-learning-section">
        <div><p className="eyebrow">Assigned to you</p><h2>Learning pathways</h2></div>
        <span data-testid="assigned-course-count">{dashboard.courses.length} courses</span>
      </section>
      <section className="course-grid" data-testid="assigned-courses-grid">
        {dashboard.courses.map((course) => <CourseCard course={course} key={course.id} testPrefix="learner-course" />)}
      </section>
      <section className="certificate-band" id="certificates" data-testid="certificates-section">
        <Certificate aria-hidden="true" size={32} weight="duotone" />
        <div><p className="eyebrow">Achievement record</p><h2>Your certificates appear here</h2></div>
        <span data-testid="certificate-empty-state">Complete a pathway and pass its assessment to receive a certificate.</span>
        <ArrowRight aria-hidden="true" size={24} />
      </section>
    </RoleLayout>
  );
}